/**
 * Custom error types for the application
 */

/** Base application error with code and context */
export class AppError extends Error {
  readonly code: string;
  readonly context?: Record<string, unknown>;
  readonly timestamp: Date;
  readonly isRetryable: boolean;

  constructor(
    message: string,
    code: string,
    options?: {
      cause?: Error;
      context?: Record<string, unknown>;
      isRetryable?: boolean;
    }
  ) {
    super(message, { cause: options?.cause });
    this.name = 'AppError';
    this.code = code;
    this.context = options?.context;
    this.timestamp = new Date();
    this.isRetryable = options?.isRetryable ?? false;
  }
}

/** Network/API errors (Supabase, fetch, etc.) */
export class NetworkError extends AppError {
  readonly statusCode?: number;

  constructor(
    message: string,
    options?: {
      cause?: Error;
      statusCode?: number;
      context?: Record<string, unknown>;
      isRetryable?: boolean;
    }
  ) {
    super(message, 'NETWORK_ERROR', {
      cause: options?.cause,
      context: { ...options?.context, statusCode: options?.statusCode },
      isRetryable: options?.isRetryable ?? true, // Network errors are usually retryable
    });
    this.name = 'NetworkError';
    this.statusCode = options?.statusCode;
  }
}

/** Database/Supabase errors */
export class DatabaseError extends AppError {
  readonly operation: string;

  constructor(
    message: string,
    operation: string,
    options?: {
      cause?: Error;
      context?: Record<string, unknown>;
      isRetryable?: boolean;
    }
  ) {
    super(message, 'DATABASE_ERROR', {
      cause: options?.cause,
      context: { ...options?.context, operation },
      isRetryable: options?.isRetryable ?? true,
    });
    this.name = 'DatabaseError';
    this.operation = operation;
  }
}

/** Authentication errors */
export class AuthError extends AppError {
  constructor(
    message: string,
    options?: {
      cause?: Error;
      context?: Record<string, unknown>;
    }
  ) {
    super(message, 'AUTH_ERROR', {
      cause: options?.cause,
      context: options?.context,
      isRetryable: false, // Auth errors should not be auto-retried
    });
    this.name = 'AuthError';
  }
}

/** Validation errors */
export class ValidationError extends AppError {
  readonly field?: string;

  constructor(
    message: string,
    options?: {
      field?: string;
      context?: Record<string, unknown>;
    }
  ) {
    super(message, 'VALIDATION_ERROR', {
      context: { ...options?.context, field: options?.field },
      isRetryable: false,
    });
    this.name = 'ValidationError';
    this.field = options?.field;
  }
}

/** Cache-related errors */
export class CacheError extends AppError {
  constructor(
    message: string,
    options?: {
      cause?: Error;
      context?: Record<string, unknown>;
    }
  ) {
    super(message, 'CACHE_ERROR', {
      cause: options?.cause,
      context: options?.context,
      isRetryable: true,
    });
    this.name = 'CacheError';
  }
}

/** Error codes for user-friendly messages */
export const ERROR_MESSAGES: Record<string, string> = {
  NETWORK_ERROR: 'Connection error. Please check your internet connection.',
  DATABASE_ERROR: 'Database error. Please try again.',
  AUTH_ERROR: 'Authentication error. Please sign in again.',
  VALIDATION_ERROR: 'Invalid input. Please check your data.',
  CACHE_ERROR: 'Failed to load cached data.',
  UNKNOWN_ERROR: 'An unexpected error occurred.',
};

/** Get user-friendly error message */
export function getUserMessage(error: unknown): string {
  if (error instanceof AppError) {
    return ERROR_MESSAGES[error.code] ?? error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return ERROR_MESSAGES.UNKNOWN_ERROR;
}

/** Check if an error is retryable */
export function isRetryable(error: unknown): boolean {
  if (error instanceof AppError) {
    return error.isRetryable;
  }
  // Network errors from fetch are usually retryable
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }
  return false;
}

/** Wrap a Supabase error into a typed error */
export function fromSupabaseError(
  error: { message: string; code?: string; details?: string },
  operation: string
): DatabaseError {
  return new DatabaseError(error.message, operation, {
    context: { code: error.code, details: error.details },
    isRetryable: !error.code?.startsWith('PGRST'), // Postgres REST errors are usually not retryable
  });
}
